import { useEffect, useState } from 'react';

import { Button } from '../../../shared/Button';
import { PortalPollResults } from '../../components/PortalPollResults';
import { getDefaultMultiChoiceAlternative } from '../../model/components/PollArticlePage';
import { isTocaIcon, TocaIcon } from '../../model/components/TocaIcon';
import { PathContextProvider } from '../../PathContext';
import { usePortalPostEditor } from '../../Post';
import { ColorInput } from './ColorInput';
import { FileInput } from './FileInput';
import { TocaIconInput } from './TocaIconInput';
import { TranslationTableInput } from './TranslationTableInput';
import {
    AlternativeBackgroundType,
    hexToUnityColor,
    isAlternativeBackgroundType,
    MultiChoicePollAlternative,
    MultiChoicePollData,
    TranslationTable,
    UnityColor,
} from '@lifeserver/shared';

import styles from './PollMultiChoiceInput.module.scss';

export const MULTI_CHOICE_ALLOWED_BACKGROUND_COLORS: UnityColor[] = (
    [
        '#000000',
        '#DDE4F0',
        '#FBBD4A',
        '#FF7040',
        '#FF5B5B',
        '#FF8CBC',
        '#7D7DF9',
        '#4DA8F5',
        '#2AC87E',
    ] as const
).map(hexToUnityColor);

export interface PollMultiChoiceInputProps {
    value: MultiChoicePollData;
    onChange?: (value: MultiChoicePollData) => void;
}

export const PollMultiChoiceInput = ({
    value,
    onChange,
}: PollMultiChoiceInputProps) => {
    const { isEditingExistingPost, initialPostState, post } =
        usePortalPostEditor();

    // Read-only mode when initial post state is 'ready'
    const isReadOnlyMode =
        isEditingExistingPost && initialPostState === 'ready';

    // Get max alternatives based on post size
    const maxAlternatives = post.size === 'Medium' ? 2 : 4;
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const handleToggle = () => {
        if (isOpen) {
            setIsClosing(true);
            setTimeout(() => {
                setIsClosing(false);
                setIsOpen(false);
            }, 300);
        } else {
            setIsOpen(true);
        }
    };

    const addAlternative = () => {
        if (value.alternatives.length < maxAlternatives) {
            const newAlternatives = [...value.alternatives];
            newAlternatives.push(getDefaultMultiChoiceAlternative());
            onChange?.({
                ...value,
                alternatives: newAlternatives,
            });
        }
    };

    const removeAlternative = (index: number) => {
        const newAlternatives = [...value.alternatives];
        newAlternatives.splice(index, 1);
        onChange?.({
            ...value,
            alternatives: newAlternatives,
        });
    };

    const onQuestionChange = (question: TranslationTable) => {
        onChange?.({
            ...value,
            question: question,
        });
    };

    const onUrlChange = (index: number, url: string) => {
        const newAlternatives = [...value.alternatives];
        newAlternatives[index] = {
            ...newAlternatives[index],
            url: url,
            iconName: undefined,
        };
        onChange?.({
            ...value,
            alternatives: newAlternatives,
        });
    };

    const onIconChange = (index: number, icon: TocaIcon) => {
        const newAlternatives = [...value.alternatives];
        newAlternatives[index] = {
            ...newAlternatives[index],
            url: undefined,
            iconName: icon,
        };
        onChange?.({
            ...value,
            alternatives: newAlternatives,
        });
    };

    const onBackgroundColorChange = (
        index: number,
        backgroundColor: UnityColor,
    ) => {
        const newAlternatives = [...value.alternatives];
        newAlternatives[index] = {
            ...newAlternatives[index],
            backgroundColor: backgroundColor,
        };
        onChange?.({
            ...value,
            alternatives: newAlternatives,
        });
    };

    const onBackgroundTypeChange = (
        index: number,
        type: AlternativeBackgroundType,
    ) => {
        const newAlternatives = [...value.alternatives];
        newAlternatives[index] = {
            ...newAlternatives[index],
            backgroundType: type,
        };
        onChange?.({
            ...value,
            alternatives: newAlternatives,
        });
    };

    return (
        <div className={styles.pollContainer}>
            {isReadOnlyMode && (
                <fieldset>
                    <legend>Results</legend>
                    <PortalPollResults
                        pollId={value.id}
                        pollType="MultiChoice"
                        alternatives={value.alternatives}
                    />
                </fieldset>
            )}

            {isReadOnlyMode ? (
                <fieldset>
                    <legend>Settings</legend>
                    <div className={styles.accordionWrapper}>
                        <Button
                            type="button"
                            onClick={handleToggle}
                            style={{
                                width: '100%',
                                textAlign: 'left',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom:
                                    isOpen || isClosing ? '16px' : '0',
                                backgroundColor: '#E8F4FD',
                                borderColor: '#74B9FF',
                                color: '#2D3436',
                                fontWeight: '500',
                            }}
                        >
                            🔧 View Poll (read only)
                            <span>{isOpen && !isClosing ? '▲' : '▼'}</span>
                        </Button>

                        {(isOpen || isClosing) && (
                            <div
                                className={
                                    isClosing
                                        ? styles.accordionContentClosing
                                        : styles.accordionContent
                                }
                            >
                                <div className={styles.readOnlyOverlay}>
                                    <div className={styles.readOnlyBanner}>
                                        This poll has already started! Published
                                        polls can't be changed
                                    </div>
                                    <PollConfigurationFields
                                        value={value}
                                        onIconChange={onIconChange}
                                        onBackgroundColorChange={
                                            onBackgroundColorChange
                                        }
                                        onBackgroundTypeChange={
                                            onBackgroundTypeChange
                                        }
                                        onUrlChange={onUrlChange}
                                        onQuestionChange={onQuestionChange}
                                        onAddAlternative={addAlternative}
                                        onRemoveAlternative={removeAlternative}
                                        maxAlternatives={maxAlternatives}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </fieldset>
            ) : (
                <PollConfigurationFields
                    value={value}
                    onIconChange={onIconChange}
                    onBackgroundColorChange={onBackgroundColorChange}
                    onBackgroundTypeChange={onBackgroundTypeChange}
                    onUrlChange={onUrlChange}
                    onQuestionChange={onQuestionChange}
                    onAddAlternative={addAlternative}
                    onRemoveAlternative={removeAlternative}
                    maxAlternatives={maxAlternatives}
                />
            )}
        </div>
    );
};

interface PollConfigurationFieldsProps {
    value: MultiChoicePollData;
    onUrlChange: (index: number, url: string) => void;
    onIconChange: (index: number, icon: TocaIcon) => void;
    onBackgroundColorChange: (index: number, color: UnityColor) => void;
    onBackgroundTypeChange: (
        index: number,
        type: AlternativeBackgroundType,
    ) => void;
    onQuestionChange: (question: TranslationTable) => void;
    onAddAlternative: () => void;
    onRemoveAlternative: (index: number) => void;
    maxAlternatives: number;
}
const PollConfigurationFields = ({
    value,
    onIconChange,
    onBackgroundColorChange,
    onBackgroundTypeChange,
    onUrlChange,
    onQuestionChange,
    onAddAlternative,
    onRemoveAlternative,
    maxAlternatives,
}: PollConfigurationFieldsProps) => {
    const [activeAlternativeIndex, setActiveAlternativeIndex] = useState(0);
    // Ensure we have at least 2 alternatives
    const alternatives =
        value.alternatives.length >= 2
            ? value.alternatives
            : [
                  ...value.alternatives,
                  getDefaultMultiChoiceAlternative(),
                  getDefaultMultiChoiceAlternative(),
              ].slice(0, 2);

    return (
        <>
            <fieldset>
                <legend>Question</legend>
                <PathContextProvider segment="question">
                    <TranslationTableInput
                        value={value.question}
                        onChange={(text) => onQuestionChange(text)}
                    />
                </PathContextProvider>
            </fieldset>

            <fieldset>
                <legend>Alternatives</legend>
                <div className={styles.alternativeTabs}>
                    {alternatives.map((_, index) => (
                        <button
                            key={index}
                            type="button"
                            className={`${styles.alternativeTab} ${
                                activeAlternativeIndex === index
                                    ? styles.activeAlternativeTab
                                    : ''
                            }`}
                            onClick={() => setActiveAlternativeIndex(index)}
                        >
                            Alt {index + 1}
                            <button
                                type="button"
                                className={styles.removeAlternativeButton}
                                disabled={alternatives.length <= 2}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveAlternative(index);
                                }}
                                title={
                                    alternatives.length <= 2
                                        ? 'Cannot remove - minimum 2 alternatives required'
                                        : `Remove Alternative ${index + 1}`
                                }
                            >
                                x
                            </button>
                        </button>
                    ))}
                    {alternatives.length < maxAlternatives && (
                        <button
                            type="button"
                            onClick={onAddAlternative}
                            disabled={alternatives.length >= maxAlternatives}
                            className={styles.plusAlternativeTab}
                            title="Add new alternative"
                        >
                            +
                        </button>
                    )}
                </div>

                <div className={styles.alternativeContent}>
                    <fieldset>
                        <legend>
                            Alternative {activeAlternativeIndex + 1}
                        </legend>

                        <fieldset>
                            <legend>Background Color</legend>
                            <div className={styles.backgroundColorContainer}>
                                <ColorInput
                                    value={
                                        alternatives[activeAlternativeIndex]
                                            ?.backgroundColor
                                    }
                                    onChange={(color) =>
                                        onBackgroundColorChange(
                                            activeAlternativeIndex,
                                            color,
                                        )
                                    }
                                    allowedColors={
                                        MULTI_CHOICE_ALLOWED_BACKGROUND_COLORS
                                    }
                                    layout="grid"
                                    gridColumns={5}
                                    allowCustomColor={true}
                                />
                            </div>
                        </fieldset>

                        <fieldset>
                            <legend>Background Type</legend>
                            <div className={styles.inputGroup}>
                                <select
                                    value={
                                        isAlternativeBackgroundType(
                                            alternatives[activeAlternativeIndex]
                                                ?.backgroundType,
                                        )
                                            ? alternatives[
                                                  activeAlternativeIndex
                                              ]?.backgroundType
                                            : ''
                                    }
                                    onChange={(e) =>
                                        isAlternativeBackgroundType(
                                            e.target.value,
                                        ) &&
                                        onBackgroundTypeChange(
                                            activeAlternativeIndex,
                                            e.target.value,
                                        )
                                    }
                                >
                                    <option value="plain">Plain</option>
                                    <option value="radialGradient">
                                        Radial Gradient
                                    </option>
                                    <option value="lightRays">
                                        Light Rays
                                    </option>
                                </select>
                            </div>
                        </fieldset>

                        <AssetSelector
                            alternative={alternatives[activeAlternativeIndex]}
                            index={activeAlternativeIndex}
                            onIconChange={onIconChange}
                            onUrlChange={onUrlChange}
                        />
                    </fieldset>
                </div>
            </fieldset>
        </>
    );
};

interface AssetSelectorProps {
    alternative?: MultiChoicePollAlternative;
    index: number;
    onIconChange: (index: number, icon: TocaIcon) => void;
    onUrlChange: (index: number, url: string) => void;
}

const AssetSelector = ({
    alternative,
    index,
    onIconChange,
    onUrlChange,
}: AssetSelectorProps) => {
    const [useIcon, setUseIcon] = useState(() => {
        if (alternative?.iconName) return true;
        if (alternative?.url) return false;
        return true;
    });

    useEffect(() => {
        if (alternative?.iconName) {
            setUseIcon(true);
        } else if (alternative?.url) {
            setUseIcon(false);
        } else {
            setUseIcon(true);
        }
    }, [alternative]);

    return (
        <fieldset>
            <legend>Asset</legend>
            <div className={styles.assetInputContainer}>
                {useIcon ? (
                    <TocaIconInput
                        selectedIcon={
                            isTocaIcon(alternative?.iconName)
                                ? alternative?.iconName
                                : ''
                        }
                        onIconChange={(icon) => onIconChange(index, icon)}
                    />
                ) : (
                    <FileInput
                        onChange={(file) => {
                            if (file) {
                                onUrlChange(index, file.url);
                            }
                        }}
                        value={
                            alternative?.url
                                ? {
                                      url: alternative.url,
                                      fileType: 'image',
                                  }
                                : undefined
                        }
                        allowedFileTypes={'image'}
                    />
                )}
            </div>
            <div className={styles.inputGroup}>
                <select
                    value={useIcon ? 'icon' : 'asset'}
                    onChange={(e) => {
                        setUseIcon(e.target.value === 'icon');
                    }}
                >
                    <option value="icon">Icon</option>
                    <option value="asset">Image</option>
                </select>
            </div>
        </fieldset>
    );
};
